import { HttpClient, HttpClientRequest } from "@effect/platform";
import { Duration, Effect } from "effect";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ACTION, BODY, HEADING, PAGE } from "./error-pages.tsx";
import { runtime } from "./runtime.ts";

// Where `UnauthenticatedPage` stashes the path a signed-out visitor landed on, so `AuthGate` can
// return them to it once the probe next resolves 2xx (deep-link preservation through the Okta
// round-trip — Decision 7's `/login` rule always redirects to `/`, so nothing ALB-side carries this).
const REDIRECT_STORAGE_KEY = "curriculum-studio:post-login-redirect";

// A denial screen the visitor never acts on (they close the tab, navigate away, or the session
// merely lapses and later recovers on its own) leaves this stash behind for the rest of the tab's
// sessionStorage lifetime. Bounding it to comfortably longer than a real Okta round-trip keeps a
// stale stash from hijacking an unrelated later "clear" mount in the same tab.
const REDIRECT_STASH_TTL = Duration.minutes(5);

type RedirectStash = { readonly path: string; readonly stashedAt: number };

// Consumes (reads + clears) the stash left by `UnauthenticatedPage`, discarding it if it has aged
// past `REDIRECT_STASH_TTL` — see the constant's comment for why "present" alone isn't enough.
const consumeRedirectStash = (): string | null => {
  const raw = window.sessionStorage.getItem(REDIRECT_STORAGE_KEY);
  if (raw === null) return null;
  window.sessionStorage.removeItem(REDIRECT_STORAGE_KEY);

  let stash: RedirectStash;
  try {
    stash = JSON.parse(raw) as RedirectStash;
  } catch {
    return null;
  }
  const age = Date.now() - stash.stashedAt;
  return age >= 0 && age < Duration.toMillis(REDIRECT_STASH_TTL) ? stash.path : null;
};

// Well below CloudFront's 30-60s origin-response ceiling (Unit 1) — a probe this slow is treated the
// same as a network error (render `children`, defer to existing per-fetch error handling) rather than
// leaving the gate's pending state unresolved indefinitely.
const PROBE_TIMEOUT = Duration.seconds(6);

// Avoids a loading-indicator flash on the common fast-probe case while still giving feedback if the
// CloudFront->ALB->OIDC->Bun round trip is slow.
const LOADING_DELAY_MS = 300;

type ProbeOutcome = "denied" | "clear";

// One lightweight authenticated GET (an existing route, not a new one) is the only channel that
// carries a real 401/403 — see the plan's "Why REST, not the WebSocket close code" note: a WS close
// code 1006 cannot distinguish a deny from a transient drop. Any outcome other than 401/403 (2xx, a
// network error, a timeout, some other non-2xx) collapses to "clear" — `AuthGate` only special-cases
// the specific "unauthenticated" signal and leaves every other failure to existing per-route/per-fetch
// error handling.
export const runAuthProbe = (): Promise<ProbeOutcome> =>
  runtime.runPromise(
    Effect.gen(function* () {
      const client = yield* HttpClient.HttpClient;
      // CON-1186: a document view re-runs this probe reactively (see `AuthProbeContext` below) whenever
      // the live WS connection's own watchdog reports the socket offline, so a cached 2xx from before
      // the session expired must never stand in for a fresh check.
      const request = HttpClientRequest.get("/api/curricula").pipe(
        HttpClientRequest.setHeader("Cache-Control", "no-store"),
      );
      const response = yield* client.execute(request);
      return response.status === 401 || response.status === 403
        ? ("denied" as const)
        : ("clear" as const);
    }).pipe(
      Effect.scoped,
      Effect.timeout(PROBE_TIMEOUT),
      Effect.catchAll(() => Effect.succeed("clear" as const)),
    ),
  );

// The logged-out screen `AuthGate` renders in place of the whole route tree on a 401/403 (Unit 5). A
// plain `<a>`, never a router `<Link>` — it must leave the SPA to hit the ALB's `/login` rule
// (Decision 7), which a client-side route change cannot do.
export const UnauthenticatedPage = () => {
  useEffect(() => {
    const stash: RedirectStash = {
      path: window.location.pathname + window.location.search,
      stashedAt: Date.now(),
    };
    window.sessionStorage.setItem(REDIRECT_STORAGE_KEY, JSON.stringify(stash));
  }, []);

  return (
    <div className={PAGE}>
      <h1 className={HEADING}>Signed out</h1>
      <p className={BODY}>Your session has ended or hasn’t started yet.</p>
      <a href="/login" className={ACTION}>
        Log in
      </a>
    </div>
  );
};

// CON-1186: the reactive re-probe trigger `AuthGate` provides to a live document connection (only
// `routes.tsx`'s `useLiveDocument` calls it, via `useReportPossibleAuthIssue` below) — there is no
// index-page equivalent, since an index page never opens a WS and so never has anything to react to.
// Defaults to a no-op so a caller outside `AuthGate` (e.g. a test rendering a component in isolation)
// doesn't need a wrapper just to satisfy the type.
const AuthProbeContext = createContext<() => void>(() => {});

export const AuthProbeProvider = AuthProbeContext.Provider;

// Called by a live document connection's own liveness watchdog the moment it sees the socket go
// offline (a missed pong past `ws-client.ts`'s `OFFLINE_THRESHOLD_MS`) — see that module's "Why REST,
// not the WebSocket close code" rationale for why a REST reprobe, not the close code, is what
// disambiguates an ALB session deny from a transient drop. Never polled on a schedule: an index page
// with no WS connected never calls this, and a document page only calls it in response to the watchdog
// actually flagging trouble.
export const useReportPossibleAuthIssue = (): (() => void) => useContext(AuthProbeContext);

// Gates the route tree behind the REST probe (Unit 5). Rendered inside `AppErrorBoundary` (so a
// probe-fetch throw still hits it) and inside `BrowserRouter` (so `UnauthenticatedPage`'s link is a
// real `<a>`, and so the deep-link restore below can call `useNavigate`).
export const AuthGate = ({ children }: { readonly children: ReactNode }) => {
  const [outcome, setOutcome] = useState<ProbeOutcome | "pending" | "loading">("pending");
  const navigate = useNavigate();

  // Deliberately `[]`: on the declarative `<BrowserRouter>` (not a data router), `useNavigate`'s
  // return value is re-created on every location change (its `useCallback` depends on the current
  // pathname), so keying this effect on `navigate` would re-run the probe on every client-side
  // navigation. It must run exactly once, on mount, per the plan's "probes once" contract.
  useEffect(() => {
    let cancelled = false;
    const loadingTimer = window.setTimeout(() => {
      if (!cancelled) setOutcome((current) => (current === "pending" ? "loading" : current));
    }, LOADING_DELAY_MS);

    runAuthProbe().then((result) => {
      window.clearTimeout(loadingTimer);
      if (!cancelled) setOutcome(result);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(loadingTimer);
    };
  }, []);

  // Split from the probe effect above so the redirect-restore logic can safely depend on the
  // unstable `navigate` identity: re-running this effect after the stored key is already cleared
  // is a no-op (`redirectTo` reads `null`), so the identity churn is harmless here.
  useEffect(() => {
    if (outcome !== "clear") return;
    const redirectTo = consumeRedirectStash();
    if (redirectTo !== null) navigate(redirectTo);
  }, [outcome, navigate]);

  // CON-1186: an Okta/ALB session expiring mid-visit never re-runs the mount-only probe above, so
  // without this the visitor's own writes would just start silently failing (the WS layer never
  // re-authenticates per frame either — see `ws.ts`'s upgrade-time-only identity resolution). Fires
  // only on demand (see `AuthProbeContext` above), never on a schedule — a stable identity across
  // renders, since it is handed down through context to a descendant that may re-render independently.
  // Only ever moves `clear` -> `denied`, never back: once `outcome` stops being `"clear"`, `children`
  // (and any live document connection inside it) unmounts, so nothing can call this again.
  const reportPossibleAuthIssue = useCallback(() => {
    runAuthProbe().then((result) => {
      if (result === "denied") setOutcome("denied");
    });
  }, []);

  if (outcome === "denied") return <UnauthenticatedPage />;
  if (outcome === "pending") return null;
  if (outcome === "loading") {
    return (
      <div className={PAGE}>
        <p className={BODY}>Loading…</p>
      </div>
    );
  }
  return <AuthProbeProvider value={reportPossibleAuthIssue}>{children}</AuthProbeProvider>;
};
